<template>
  <div class="service-wrap pt-20 pb-20">
    <div class="container">
      <!-- En-tête -->
      <div class="row mb-5">
        <div class="col-12 text-center">
          <h2 class="section-title">Bibliothèque de Vidéos</h2>
          <p class="section-subtitle">Découvrez nos ressources vidéo éducatives</p>
        </div>
      </div>

      <!-- État de chargement -->
      <div v-if="isLoading" class="loading-container">
        <div class="spinner"></div>
        <p class="loading-text">Chargement des vidéos en cours...</p>
        <div class="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>

      <!-- Message quand aucune vidéo -->
      <div v-else-if="videos.length === 0" class="empty-state">
        <div class="empty-icon">
          <i class="flaticon-video"></i>
        </div>
        <h3 class="empty-title">Aucune vidéo disponible pour le moment</h3>
        <p class="empty-description">
          Nous travaillons actuellement sur l'ajout de contenu vidéo éducatif. 
          Revenez bientôt pour découvrir nos nouvelles ressources pédagogiques !
        </p>
        <div class="coming-soon-badge">
          <span> Contenu en préparation</span>
        </div>
      </div>

      <!-- Grille de vidéos -->
      <div v-else class="row">
        <div 
          class="col-xl-4 col-lg-6 col-md-6 mb-4" 
          v-for="(video, index) in videos" 
          :key="index"
        >
          <div class="video-card">
            <div class="video-thumbnail">
              <img :src="video.thumbnail" alt="Video thumbnail" />
              <div class="play-button">
                <i class="flaticon-play"></i>
              </div>
              <div class="duration-badge">
                <i class="flaticon-clock"></i> {{ video.duree }}
              </div>
            </div>
            <div class="video-info">
              <h4 class="video-title">{{ video.titre }}</h4>
              <p class="video-description">{{ video.description }}</p>
              <div class="video-meta">
                <span class="views">
                  <i class="flaticon-eye"></i> {{ video.vues }} vues
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div class="mt-5" v-if="videos.length > 0 && totalPages > 1">
        <PaginationComponent
          :page="page"
          :totalPages="totalPages"
          :totalElements="totalElements"
          :limit="limit"
          @paginate="handlePaginate"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import ApiService from "@/services/ApiService";
import { error } from "@/utils/utils";
import { defineComponent, ref, onMounted } from "vue";
import PaginationComponent from "@/components/Utilities/Pagination.vue";

interface Video {
  id: number;
  titre: string;
  description: string;
  thumbnail: string;
  duree: string;
  vues: number;
}

export default defineComponent({
  name: "ViDeo",
  components: { PaginationComponent },
  setup() {
    const videos = ref<Array<Video>>([]);
    const isLoading = ref(false);

    // Pagination
    const page = ref(1);
    const totalPages = ref(0);
    const limit = ref(12);
    const totalElements = ref(0);

    const handlePaginate = ({ page_, limit_ }) => {
      page.value = page_;
      getVideos(page_, limit_);
    };

    // Charger les vidéos
    function getVideos(pageNum = 1, limi = 12) {
      isLoading.value = true;
      
      // Simuler un délai de chargement réaliste
      setTimeout(() => {
        // Décommenter quand tu auras des vidéos
        // return ApiService.get(`/all/videos?page=${pageNum}&limit=${limi}`)
        //   .then(({ data }) => {
        //     videos.value = data.data.data;
        //     totalPages.value = data.data.totalPages;
        //     limit.value = data.data.limit;
        //     totalElements.value = data.data.totalElements;
        //   })
        //   .catch(({ response }) => {
        //     error(response?.data?.message || "Erreur lors du chargement des vidéos");
        //     videos.value = [];
        //   })
        //   .finally(() => {
        //     isLoading.value = false;
        //   });
        
        // Pour l'instant, on simule un retour vide
        videos.value = [];
        totalPages.value = 0;
        totalElements.value = 0;
        isLoading.value = false;
      }, 1500);
    }

    onMounted(() => {
      getVideos();
    });

    return {
      videos,
      isLoading,
      page,
      totalPages,
      limit,
      totalElements,
      handlePaginate,
    };
  },
});
</script>

<style scoped>
.service-wrap {
  background-color: #f8f9fa;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 1rem;
}

.section-subtitle {
  font-size: 1.1rem;
  color: #6c757d;
  margin-bottom: 2rem;
}

/* Container de chargement */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 40px 20px;
}

/* Spinner animé - Bleu */
.spinner {
  width: 60px;
  height: 60px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #0b1b3f;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  font-size: 18px;
  color: #666;
  margin-bottom: 15px;
  font-weight: 500;
}

/* Points de chargement animés - Bleu */
.loading-dots {
  display: flex;
  gap: 8px;
}

.loading-dots span {
  width: 10px;
  height: 10px;
  background-color: #0b1b3f;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out both;
}

.loading-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.loading-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* État vide */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 500px;
  padding: 60px 20px;
  text-align: center;
}

.empty-icon {
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #0b1b3f 0%, #0b1b3f 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
  animation: pulse 2s infinite;
}

.empty-icon i {
  font-size: 60px;
  color: white;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 #0b1b3f;
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 20px rgba(0, 123, 255, 0);
  }
}

.empty-title {
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 15px;
}

.empty-description {
  font-size: 16px;
  color: #6c757d;
  max-width: 600px;
  line-height: 1.6;
  margin-bottom: 30px;
}

.coming-soon-badge {
  display: inline-block;
  background: linear-gradient(135deg, #f5c518 0%, #f5c518 100%);
  color: #fff;
  padding: 12px 30px;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  box-shadow: 0 4px 15px rgba(255, 193, 7, 0.4);
  animation: fadeInUp 0.6s ease;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Cards de vidéos */
.video-card {
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  cursor: pointer;
}

.video-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.video-thumbnail {
  position: relative;
  width: 100%;
  height: 220px;
  overflow: hidden;
  background: linear-gradient(135deg,#0b1b3f 0%,#0b1b3f 100%);
}

.video-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.video-card:hover .video-thumbnail img {
  transform: scale(1.1);
}

.play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 2;
}

.video-card:hover .play-button {
  background: #f5c518;
  transform: translate(-50%, -50%) scale(1.15);
}

.play-button i {
  font-size: 24px;
  color:#0b1b3f;
  margin-left: 3px;
}

.video-card:hover .play-button i {
  color: white;
}

.duration-badge {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
  z-index: 1;
}

.duration-badge i {
  font-size: 12px;
}

.video-info {
  padding: 25px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.video-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 12px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 56px;
  line-height: 1.4;
}

.video-description {
  font-size: 0.9rem;
  color: #6c757d;
  margin-bottom: 15px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 40px;
  line-height: 1.5;
  flex: 1;
}

.video-meta {
  display: flex;
  align-items: center;
  padding-top: 15px;
  border-top: 1px solid #e9ecef;
}

.views {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 500;
}

.views i {
  font-size: 16px;
  color:#0b1b3f;
}

/* Responsive */
@media (max-width: 768px) {
  .section-title {
    font-size: 2rem;
  }

  .empty-title {
    font-size: 24px;
  }
  
  .empty-description {
    font-size: 14px;
  }
  
  .empty-icon {
    width: 100px;
    height: 100px;
  }
  
  .empty-icon i {
    font-size: 50px;
  }

  .video-title {
    font-size: 1.1rem;
    min-height: auto;
  }

  .video-thumbnail {
    height: 200px;
  }
}
</style>